import { reportsControlStyles } from '@/common/components/Select/styles';
import SelectController from '@/common/components/SelectController';
import TextField from '@/common/components/TextField';
import { DatePicker } from '@/common/components/ui/datePicker';
import { useCities } from '@/common/hooks/queries/useCities';
import { useProducts } from '@/common/hooks/queries/useProducts';
import useDebounce from '@/common/hooks/useDebounce';
import { GeoTransformers } from '@/common/services/geoTransformers';
import { Mapper } from '@/common/services/mapper';
import { ProductData } from '@/common/types/product.type';
import { filterLabel } from '@/features/reports/constants/filterLabel.constant';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PermittedReportFilterData } from '../types/permittedReports.type';

interface FilterDefaultFieldType {
  [key: string]: (props: {
    control: Control;
    errors: FieldErrors;
    filterName: string;
    value: string;
    filterProps: PermittedReportFilterData;
    setFilterParams: (mutationFn: (searchParams: any) => any) => void;
  }) => React.ReactNode;
}

export const defaultReportsFilterFields: FilterDefaultFieldType = {
  textField: ({ control, filterName, value, setFilterParams }) => (
    <Controller
      control={control}
      name={filterName}
      render={({ field: { onChange, ...rest } }) => (
        <TextField
          {...rest}
          label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
          className="w-full"
          value={value}
          onChange={(event: any) => {
            onChange(event);
            setFilterParams((searchParams) => ({
              ...searchParams,
              q: { ...searchParams.q, [filterName]: event.target.value },
            }));
          }}
        />
      )}
    />
  ),
  dateField: ({ control, filterName, value, setFilterParams }) => (
    <Controller
      control={control}
      name={filterName}
      render={({ field }) => (
        <DatePicker
          name={filterName}
          label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
          date={!!value ? new Date(value) : undefined}
          setDate={(value) => {
            field.onChange(value);
            setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: value } }));
          }}
          variant="default"
          className="w-full"
          inputClassName="h-[52px] text-sm rounded-md rounded-r-none bg-neutral-op-8 hover:bg-neutral-op-8"
        />
      )}
    />
  ),
  citySelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchCity, setSearchCity] = useDebounce('');

    const { data: citiesOptions, isFetching: citiesIsFetching } = useCities({
      filters: {
        search: searchCity,
      },
      mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchCity}
        options={citiesOptions}
        isLoading={citiesIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
  productSelectField: ({ control, errors, filterName, value, setFilterParams, filterProps }) => {
    const [searchProduct, setSearchProduct] = useDebounce('');

    const { data: productsOptions, isFetching: productsIsFetching } = useProducts({
      filters: {
        search: searchProduct,
      },
      mapper: Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
    });

    return (
      <SelectController
        control={control}
        name={filterName}
        label={filterLabel[(filterName as keyof typeof filterLabel) || 'default']}
        className='w-full'
        markAsRequired={filterProps.required}
        onInputChange={setSearchProduct}
        options={productsOptions}
        isLoading={productsIsFetching}
        value={value}
        triggerOnChange={(newValue) => setFilterParams((searchParams) => ({ ...searchParams, q: { ...searchParams.q, [filterName]: newValue } }))}
        errorMessage={errors[filterName]?.message as string | undefined || undefined}
        customStyles={reportsControlStyles}
      />
    );
  },
};
